import { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Video, Clock } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '--';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
};

const formatTime = (isoStr) => {
  const d = new Date(isoStr);
  const now = new Date();
  const diff = (now - d) / 1000;

  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return d.toLocaleDateString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function CallHistory() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchCalls = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/calls/history?page=${page}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalls(res.data.calls);
      setTotalPages(res.data.pages);
    } catch (err) {
      console.error('Failed to fetch call history');
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  const getCallIcon = (call) => {
    if (call.status === 'declined' || call.status === 'missed') {
      return <PhoneMissed className="w-5 h-5 text-red-500" />;
    }
    if (call.direction === 'incoming') {
      return <PhoneIncoming className="w-5 h-5 text-green-500" />;
    }
    return <PhoneOutgoing className="w-5 h-5 text-blue-500" />;
  };

  const getCallLabel = (call) => {
    if (call.status === 'missed' && call.direction === 'outgoing') return 'No answer';
    if (call.status === 'missed') return 'Missed';
    if (call.status === 'declined') return 'Declined';
    if (call.direction === 'incoming') return 'Incoming';
    return 'Outgoing';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-teal-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 pt-24 pb-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Call History</h1>
          <p className="text-gray-500 mt-1">Your recent voice and video calls</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-coral border-t-transparent" />
          </div>
        ) : calls.length === 0 ? (
          <div className="text-center py-20">
            <Phone className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-semibold text-gray-600 mb-2">No call history</h2>
            <p className="text-gray-400">Your calls will appear here</p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {calls.map(call => (
                <div
                  key={call.id}
                  data-testid={`call-history-item-${call.id}`}
                  className="bg-white rounded-xl p-4 flex items-center space-x-4 hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => navigate(`/messages/${call.other_user.id}`)}
                >
                  <Avatar className="w-12 h-12 border-2 border-gray-100">
                    <AvatarImage src={call.other_user.profile_photo} />
                    <AvatarFallback className="bg-teal text-white font-bold">
                      {call.other_user.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-gray-900 truncate">{call.other_user.name}</h3>
                      {call.call_type === 'video' && <Video className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      {getCallIcon(call)}
                      <span className={call.status === 'declined' ? 'text-red-500' : ''}>{getCallLabel(call)}</span>
                      <span>·</span>
                      <span>{formatTime(call.created_at)}</span>
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0">
                    {call.duration ? (
                      <div className="flex items-center space-x-1 text-sm text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>{formatDuration(call.duration)}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">
                        {call.status === 'declined' ? 'Missed' : '--'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center space-x-2 mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  className="rounded-full"
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="rounded-full"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
